import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import type {
  LiveChatVisitor,
  LiveChatVisitorList,
  LiveChatVisitorState,
} from '@nucleus-commerce/api-client/admin';

import { conversationsApi } from '../api/conversations-api';
import { countVisitors } from '../lib/visitors-cache';

import { conversationKeys } from './conversation-keys';
import { visitorsRefetchMs } from './live-visitors-query';

/**
 * Which visitors the panel shows: one store (the store-pinned inbox, its own route), or the
 * org-wide list, optionally narrowed by the inbox's store filter.
 */
export type VisitorsScope =
  { kind: 'store'; storeId: string } | { kind: 'org'; storeIds?: readonly string[] | undefined };

export interface LiveVisitors {
  items: LiveChatVisitor[];
  counts: Record<LiveChatVisitorState, number>;
  isPending: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * The live visitor list for the inbox's scope: one request either way (LCW1). A store filter of
 * one store asks the org route for that store; several stores ask for all and keep theirs.
 */
export function useLiveVisitors(scope: VisitorsScope, polling: boolean): LiveVisitors {
  const pinned = scope.kind === 'store' ? scope.storeId : null;
  const filter = scope.kind === 'org' ? scope.storeIds : undefined;
  const narrowed = filter?.length === 1 ? (filter[0] ?? null) : null;
  const keep = filter && filter.length > 1 ? filter : null;
  const select = useCallback(
    (list: LiveChatVisitorList): LiveChatVisitorList => {
      if (!keep) return list;
      const items = list.items.filter((item) => keep.includes(item.storeId));
      return { items, counts: countVisitors(items) };
    },
    [keep],
  );
  const query = useQuery<LiveChatVisitorList, Error, LiveChatVisitorList>({
    queryKey: pinned ? conversationKeys.visitors(pinned) : conversationKeys.orgVisitors(narrowed),
    queryFn: () =>
      pinned
        ? conversationsApi.visitors(pinned)
        : conversationsApi.orgVisitors(narrowed ?? undefined),
    refetchInterval: visitorsRefetchMs(polling),
    select,
  });
  return {
    items: query.data?.items ?? [],
    counts: query.data?.counts ?? { chatting: 0, active: 0, idle: 0 },
    isPending: query.isPending,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
